/**
 * 统一的WebSocket管理服务
 * 合并了terminal、文件监听和作业状态监听三个WebSocket连接
 */

import * as wsApi from '@/api/terminal';
import { Button, Space } from 'antd';
import { getAntdApp } from '@/utils/antdApp';
import i18n from '../../i18n';
import useFileStore from '@/utils/fileStore';
import { useLatexCompileStore } from '@/utils/latexCompileStore';

// 消息类型枚举
export enum WSMessageType {
    // Terminal messages
    TERMINAL_INIT = 'terminal_init',
    TERMINAL_INPUT = 'terminal_input',
    TERMINAL_OUTPUT = 'terminal_output',
    TERMINAL_RESIZE = 'terminal_resize',
    TERMINAL_CLOSE = 'terminal_close',

    // File watch messages
    FS_WATCH_INIT = 'fs_watch_init',
    FS_WATCH_ADD_PATHS = 'fs_watch_add_paths',
    FS_WATCH_REMOVE_PATHS = 'fs_watch_remove_paths',
    FS_WATCH_EVENT = 'fs_watch_event',
    FS_WATCH_FILE_CHANGE = 'fs_watch_file_change',
    FS_WATCH_PATHS = 'fs_watch_file_watch_paths',
    FS_WATCH_CLOSE = 'fs_watch_close',

    LATEX_COMPILE = 'latex_compile',

    DISK_USAGE_REFRESH = 'refresh',

    // Job status messages
    JOB_SUBSCRIBE = 'job_subscribe',
    JOB_UNSUBSCRIBE = 'job_unsubscribe',
    JOB_STATUS = 'job_status',

    // Control messages
    PING = 'ping',
    PONG = 'pong',
    ERROR = 'error',
    CONNECTED = 'connected', // 连接成功事件（内部事件）
    DISCONNECTED = 'disconnected', // 连接断开事件（内部事件）
    RECONNECTED = 'reconnected', // 重连成功事件（内部事件）
}

// 消息接口
export interface WSMessage {
    type: WSMessageType;
    payload?: any;
}

export interface LatexCompilePayload {
    tex_path?: string;
    output_path?: string;
    success?: boolean;
    error?: string;
}

// 消息处理器类型
export type MessageHandler = (data: any) => void;

class WebSocketService {
    private ws: WebSocket | null = null;
    private messageHandlers: Map<WSMessageType, Set<MessageHandler>> = new Map();

    // 重连配置
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectInterval = 2000; // 2秒
    private reconnectTimeoutId: NodeJS.Timeout | null = null;
    private isReconnecting = false;

    // 心跳配置
    private heartbeatInterval: NodeJS.Timeout | null = null;
    private heartbeatTimeout: NodeJS.Timeout | null = null;
    private readonly HEARTBEAT_INTERVAL = 30000; // 30秒
    private readonly HEARTBEAT_TIMEOUT = 5000; // 5秒

    // 连接状态
    private isConnected = false;
    private isInitialized = false;

    // 初始化参数（用于重连）
    private userName: string | undefined;
    private baseHome: string | undefined;
    private terminalPid: string | undefined;

    private noHandlerMsgs: WSMessage[] = [];
    private noSendMsgs: WSMessage[] = [];

    constructor() {
        console.log('[WebSocketService] Service created');
        this.registerBuiltinNotificationHandlers();
    }

    private registerBuiltinNotificationHandlers() {
        const notificationHandlers = new Map<WSMessageType, MessageHandler>([
            [WSMessageType.LATEX_COMPILE, this.handleLatexCompileNotification.bind(this)],
        ]);

        notificationHandlers.forEach((handler, type) => {
            this.on(type, handler);
        });
    }

    private handleLatexCompileNotification(payload?: LatexCompilePayload) {
        const texPath = payload?.tex_path?.trim();
        const outputPath = payload?.output_path?.trim();
        const errorText = payload?.error?.trim();

        if (texPath) {
            useLatexCompileStore.getState().finishCompile(texPath);
        }

        if (payload?.success) {
            if (errorText) {
                getAntdApp().notification.info({
                    title: i18n.t('app_setting.system_info', { ns: 'common' }),
                    description: i18n.t('editor_tabs.latex_compiling', { ns: 'common' }),
                });
                return;
            }
            const successTarget = outputPath || texPath;
            getAntdApp().notification.info({
                title: i18n.t('app_setting.system_info', { ns: 'common' }),
                description: successTarget
                    ? i18n.t('editor_tabs.latex_success_with_target', {
                        ns: 'common',
                        target: successTarget,
                        interpolation: { escapeValue: false },
                    })
                    : i18n.t('editor_tabs.latex_success', { ns: 'common' }),
                actions: <Space>
                    {outputPath && (
                        <Button
                            type="link"
                            onClick={() => {
                                useFileStore.getState().openFile(outputPath);
                            }}
                        >View Output</Button>
                    )}
                </Space>,
            })
            return;
        }

        const failedTarget = texPath || i18n.t('editor_tabs.current_file', { ns: 'common' });
        getAntdApp().notification.info({
            title: i18n.t('app_setting.system_info', { ns: 'common' }),
            description: errorText
                ? i18n.t('editor_tabs.latex_failed_with_error', {
                    ns: 'common',
                    target: failedTarget,
                    error: errorText,
                    interpolation: { escapeValue: false },
                })
                : i18n.t('editor_tabs.latex_failed_with_target', {
                    ns: 'common',
                    target: failedTarget,
                    interpolation: { escapeValue: false },
                }),
        });
    }

    /**
     * 初始化WebSocket连接
     */
    public async initialize(params: { userName?: string; baseHome?: string; terminalPid?: string }) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            console.log('[WebSocketService] Already connected');
            return;
        }

        this.userName = params.userName;
        this.baseHome = params.baseHome;
        this.terminalPid = params.terminalPid;
        this.isInitialized = true;

        await this.connect();
    }

    /**
     * 建立WebSocket连接
     */
    private async connect() {
        try {
            // 清除心跳定时器
            this.clearHeartbeat();
            this.isReconnecting = false;

            if (!this.userName) {
                throw new Error('WebSocketService: userName is required for connection');
            }
            // 构建WebSocket URL（使用统一的端点）
            const wsUrl = this.getWebSocketUrl(this.userName);

            console.log(`[WebSocketService] Connecting to ${wsUrl}`);
            this.ws = new WebSocket(wsUrl);

            this.ws.onopen = this.handleOpen.bind(this);
            this.ws.onclose = this.handleClose.bind(this);
            this.ws.onerror = this.handleError.bind(this);
            this.ws.onmessage = this.handleMessage.bind(this);

        } catch (error) {
            console.error('[WebSocketService] Connection error:', error);
            this.attemptReconnect();
        }
    }

    /**
     * 获取WebSocket URL
     */
    private getWebSocketUrl(username: string): string {
        const baseUrl = wsApi.wsUrl || window.origin + '/';
        const wsBaseUrl = baseUrl.replace('http', 'ws');

        // 使用统一的WebSocket端点
        return `${wsBaseUrl}unified-ws/${username}`;
    }

    /**
     * 处理连接打开
     */
    private handleOpen() {
        console.log('[WebSocketService] Connection established');
        const wasReconnecting = this.isReconnecting;
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.isReconnecting = false;

        const connectedHandlers = this.messageHandlers.get(WSMessageType.CONNECTED);
        if (connectedHandlers) {
            connectedHandlers.forEach(handler => {
                try {
                    handler({});
                } catch (error) {
                    console.error('[WebSocketService] Error in connected handler:', error);
                }
            });
        }

        // 启动心跳
        this.startHeartbeat();

        // 发送初始化消息（包含用户名、baseHome等信息）
        this.sendInitFsWatchMessage();
        this.sendInitPtyMessage();

        // 如果是重连成功，通知所有监听者
        if (wasReconnecting) {
            console.log('[WebSocketService] Reconnected successfully, notifying handlers');
            const handlers = this.messageHandlers.get(WSMessageType.RECONNECTED);
            if (handlers) {
                handlers.forEach(handler => {
                    try {
                        handler({});
                    } catch (error) {
                        console.error('[WebSocketService] Error in reconnect handler:', error);
                    }
                });
            }
        }

        if (this.noSendMsgs.length > 0) {
            console.log("[WebSocketService] Sending queued messages after reconnect:", this.noSendMsgs)
            this.noSendMsgs.forEach(msg => {
                this.send(msg);
            });
            this.noSendMsgs = [];
        }
    }

    /**
     * 发送初始化消息
     */
    private sendInitFsWatchMessage() {

        if (this.baseHome) {
            this.send({
                type: WSMessageType.FS_WATCH_INIT,
                payload: {
                    paths: [this.baseHome]
                },
            })
        }
    }

    private sendInitPtyMessage() {
        const initData: any = {};

        if (this.userName) {
            initData.userName = this.userName;
        }

        if (this.baseHome) {
            initData.baseHome = this.baseHome;
        }

        if (this.terminalPid) {
            initData.terminalPid = this.terminalPid;
        }

        this.send({
            type: WSMessageType.TERMINAL_INIT,
            payload: initData,
        });
    }

    /**
     * 处理连接关闭
     */
    private handleClose(event: CloseEvent) {
        console.log('[WebSocketService] Connection closed:', event);
        this.isConnected = false;
        this.clearHeartbeat();

        const disconnectedHandlers = this.messageHandlers.get(WSMessageType.DISCONNECTED);
        if (disconnectedHandlers) {
            disconnectedHandlers.forEach(handler => {
                try {
                    handler({});
                } catch (error) {
                    console.error('[WebSocketService] Error in disconnected handler:', error);
                }
            });
        }

        // 只有非正常关闭才需要重连
        if (event.code !== 1000 && !this.isReconnecting) {
            this.attemptReconnect();
        }
    }

    /**
     * 处理连接错误
     */
    private handleError(error: Event) {
        console.error('[WebSocketService] Connection error:', error);
        this.isConnected = false;
        this.clearHeartbeat();

        const disconnectedHandlers = this.messageHandlers.get(WSMessageType.DISCONNECTED);
        if (disconnectedHandlers) {
            disconnectedHandlers.forEach(handler => {
                try {
                    handler({});
                } catch (error) {
                    console.error('[WebSocketService] Error in disconnected handler:', error);
                }
            });
        }

        if (!this.isReconnecting) {
            this.attemptReconnect();
        }
    }

    /**
     * 处理收到的消息
     */
    private handleMessage(event: MessageEvent) {
        try {
            const message: WSMessage = JSON.parse(event.data);

            // 处理心跳响应
            if (message.type === WSMessageType.PONG) {
                if (this.heartbeatTimeout) {
                    clearTimeout(this.heartbeatTimeout);
                    this.heartbeatTimeout = null;
                }
                return;
            }

            // 分发消息给相应的处理器
            const handlers = this.messageHandlers.get(message.type);
            if (handlers && handlers.size > 0) {
                handlers.forEach(handler => {
                    try {
                        handler(message.payload);
                    } catch (error) {
                        console.error(`[WebSocketService] Error in message handler for ${message.type}:`, error);
                    }
                });
            } else {
                this.noHandlerMsgs.push(message);
                console.warn("[WebSocketService] No handler for current message, saved in array:", this.noHandlerMsgs)
            }

        } catch (error) {
            console.error('[WebSocketService] Error parsing message:', error);
        }
    }

    /**
     * 更新终端 PID（用于 PTY_NOT_FOUND 重新分配后更新）
     */
    public setTerminalPid(pid: string) {
        this.terminalPid = pid;
    }

    /**
     * 发送消息
     */
    public send(message: WSMessage) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[WebSocketService] Cannot send message - connection not open');
            this.noSendMsgs.push(message);
            return false;
        }

        try {
            this.ws.send(JSON.stringify(message));
            return true;
        } catch (error) {
            console.error('[WebSocketService] Error sending message:', error);
            return false;
        }
    }

    /**
     * 注册消息处理器
     */
    public on(type: WSMessageType, handler: MessageHandler) {
        if (!this.messageHandlers.has(type)) {
            this.messageHandlers.set(type, new Set());
        }

        this.messageHandlers.get(type)!.add(handler);
        console.log("[WebSocketService] Registered handler for message type:", type)
        if (this.noHandlerMsgs.length > 0) {
            const remainingMsgs: WSMessage[] = [];
            console.log("type:", type)
            console.log(this.noHandlerMsgs)
            this.noHandlerMsgs.forEach(msg => {
                if (msg.type === type) {
                    try {
                        handler(msg.payload);
                    } catch (error) {
                        console.error(`[WebSocketService] Error in message handler for ${msg.type}:`, error);
                    }
                } else {
                    remainingMsgs.push(msg);
                }
            });
            this.noHandlerMsgs = remainingMsgs;
        }

        // 返回取消订阅的函数
        return () => this.off(type, handler);
    }

    /**
     * 取消消息处理器
     */
    public off(type: WSMessageType, handler: MessageHandler) {
        const handlers = this.messageHandlers.get(type);
        if (handlers) {
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.messageHandlers.delete(type);
            }
        }
    }

    /**
     * 开始心跳检测
     */
    private startHeartbeat() {
        this.clearHeartbeat();

        this.heartbeatInterval = setInterval(() => {
            this.sendHeartbeat();
        }, this.HEARTBEAT_INTERVAL);
    }

    /**
     * 发送心跳
     */
    private sendHeartbeat() {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[WebSocketService] Cannot send heartbeat - connection not open');
            this.clearHeartbeat();
            return;
        }

        try {
            this.send({
                type: WSMessageType.PING,
            });

            // 设置超时检测
            this.heartbeatTimeout = setTimeout(() => {
                console.warn('[WebSocketService] Heartbeat timeout - connection may be dead');

                if (this.ws) {
                    try {
                        this.ws.close();
                    } catch (e) {
                        console.error('[WebSocketService] Error closing WebSocket:', e);
                    }
                    this.ws = null;
                }

                this.clearHeartbeat();

                const disconnectedHandlers = this.messageHandlers.get(WSMessageType.DISCONNECTED);
                if (disconnectedHandlers) {
                    disconnectedHandlers.forEach(handler => {
                        try {
                            handler({});
                        } catch (error) {
                            console.error('[WebSocketService] Error in disconnected handler:', error);
                        }
                    });
                }

                if (!this.isReconnecting) {
                    this.attemptReconnect();
                }
            }, this.HEARTBEAT_TIMEOUT);

        } catch (error) {
            console.error('[WebSocketService] Error sending heartbeat:', error);
            this.clearHeartbeat();

            if (!this.isReconnecting) {
                this.attemptReconnect();
            }
        }
    }

    /**
     * 清除心跳定时器
     */
    private clearHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }

        if (this.heartbeatTimeout) {
            clearTimeout(this.heartbeatTimeout);
            this.heartbeatTimeout = null;
        }
    }

    /**
     * 尝试重连
     */
    private attemptReconnect() {
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
        }

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error(`[WebSocketService] Max reconnect attempts (${this.maxReconnectAttempts}) reached`);
            getAntdApp().message.error(`Failed to reconnect after ${this.maxReconnectAttempts} attempts. Please refresh the page.`);
            const handlers = this.messageHandlers.get(WSMessageType.DISCONNECTED);
            if (handlers) {
                handlers.forEach(handler => {
                    try { handler({ exhausted: true }); } catch (e) {}
                });
            }
            return;
        }

        if (!this.isInitialized) {
            console.warn('[WebSocketService] Cannot reconnect - service not initialized');
            return;
        }

        // re-send init messages on reconnect
        this.sendInitPtyMessage();

        this.isReconnecting = true;
        this.reconnectAttempts += 1;

        console.log(`[WebSocketService] Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

        this.reconnectTimeoutId = setTimeout(async () => {
            try {
                if (this.ws) {
                    this.ws.close();
                }
                await this.connect();
            } catch (error) {
                console.error('[WebSocketService] Reconnection attempt failed:', error);
            }
        }, this.reconnectInterval);
    }

    /**
     * 手动触发重连（重置重连计数，可在 UI 上提供重试按钮时调用）
     */
    public reconnect() {
        this.reconnectAttempts = 0;
        this.isReconnecting = false;
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = null;
        }
        this.attemptReconnect();
    }

    /**
     * 断开WebSocket连接（但保留 handlers，用于重连）
     */
    public disconnect() {
        console.log('[WebSocketService] Disconnecting (handlers preserved)');

        // 清除心跳
        this.clearHeartbeat();

        // 清除重连定时器
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = null;
        }

        // 关闭WebSocket连接
        if (this.ws) {
            this.ws.close(1000, 'Service disconnected');
            this.ws = null;
        }

        // 重置连接状态（但保留 messageHandlers 和 isInitialized）
        this.isConnected = false;
        this.isReconnecting = false;
        this.reconnectAttempts = 0;
    }

    /**
     * 销毁WebSocket连接（完全清理，包括 handlers）
     */
    public destroy() {
        console.log('[WebSocketService] Destroying service');

        // 先断开连接
        this.disconnect();

        // 清除所有消息处理器
        this.messageHandlers.clear();

        // 重置初始化状态
        this.isInitialized = false;
    }

    /**
     * 检查连接状态
     */
    public isConnectedStatus(): boolean {
        return this.isConnected && this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    }

    /**
     * 获取重连尝试次数
     */
    public getReconnectAttempts(): number {
        return this.reconnectAttempts;
    }
}

// 导出单例
export const websocketService = new WebSocketService();
